import { PROJECT_FORM_TYPES, AUX_MUTATION, PROJECT_FORM_MODE } from "../projectFormStore";
import { PROJECT_FORM_CONTROLLER_TYPES } from "../projectFormControllerStore";
import { PROJECT_TYPES } from "SharedStores/Project/projectStore";
import { PROGRAM_TYPES } from "SharedStores/Program/programStore";
import { convertToFiniteNumber, isFiniteNumber } from "Util/util.js";
import { idGenerator } from "Util/uniqueIdGenerator";
import { KEYS as i18nKey, NAMESPACE as i18nNs } from "Forms/Components/Elements/i18n/incentiveSelector.i18n.js";

const inputFields = {
    SelectedIncentiveDesign: "Selected Incentive Design"
};
const outputFields = {
    CalculatedAmount: "Calculated Amount",
    CalculationDetails: "Calculation Details"
};

export const SUFFIXES = {
    inputFields,
    outputFields
};

export const NO_INCENTIVE_DESIGN = "None";
export const ADMIN_OVERRIDE_DESIGN = "ADMIN OVERRIDE";
export const NOT_YET_CALCULATED = "Not yet calculated.";
export const NOT_YET_CALCULATED_DETAILS = "No details - not yet calculated.";

const RECALC_DIALOG_ROLE = "incentive-recalc-warning";
const RECALC_CONFIRM_ROLE = "incentive-recalc-confirm";
const ELIGIBILITY_DIALOG_ROLE = "incentive-eligibility-update-warning";
const ELIGIBILITY_CONFIRM_ROLE = "incentive-eligibility-update-confirm";

const DEBOUNCE_DELAY = 250;

function i18nMessageText($app, messageKey) {
    return $app.res({
        namespace: i18nNs,
        key: messageKey
    });
}

function clearOutputValuesAsync(element, dispatch) {
    const promises = [];
    for (const key in outputFields) {
        if (outputFields.hasOwnProperty(key)) {
            const suffix = outputFields[key];
            const dataFieldId = element.GroupDataFields[suffix];
            if (suffix && dataFieldId) {
                promises.push(
                    dispatch(
                        PROJECT_FORM_TYPES.actions.setProjectData,
                        {
                            dataFieldId,
                            value: null
                        },
                        { root: true }
                    )
                );
            }
        }
    }
    return Promise.all(promises);
}

const debounceCache = {};
const delayedOperationIdGenerator = new idGenerator();

async function onDependencyUpdate({
    $app,
    baseController,
    element,
    dataFieldId,
    rootGetters,
    dispatch,
    state,
    commit
}) {
    // Ignore dependency updates in prefill mode. No prefill for incentive selector.
    // State here is the local state of the projectFormStore
    if (state.ProjectFormMode === PROJECT_FORM_MODE.Prefill) {
        return;
    }

    // Create a reference to the current projectElement state
    const projectElementStateRef = state.ProjectElements[element.PublicId];

    //add debounce entry if it's missing
    const resetDebounceCacheForElementId = function (elementIdToReset) {
        debounceCache[elementIdToReset] = {
            isUnconditional: false,
            isConditional: false,
            unconditionalDataFieldIdList: [],
            operations: {}
        };
    };
    if (!debounceCache[element.PublicId]) {
        resetDebounceCacheForElementId(element.PublicId);
    }

    // A data field can be both a conditional and unconditional dependency at the same time, so check
    //  the dataFieldId for both cases
    let isConditional =
        element.DependencyDefinitions.filter(function (d) {
            return d.DataFieldId === dataFieldId && d.IsConditional;
        }).length > 0;
    const isUnconditional =
        element.DependencyDefinitions.filter(function (d) {
            return d.DataFieldId === dataFieldId && !d.IsConditional;
        }).length > 0;

    // If the data field is both a Conditional and an Unconditional dependency, then it must appear at
    //  least *twice* in the projectElementStateRef.FieldDependencies list in order to be a Conditional dependency
    //  *for the currently selected IncentiveDesign*.  The projectElementStateRef.FieldDependencies list is
    //  updated every time the IncentiveDesign selection changes, so it reflects the dependencies for the
    //  current selection, and an Unconditional dependency will always appear exactly once in
    //  projectElementStateRef.FieldDependencies.
    if (isConditional && isUnconditional) {
        isConditional = projectElementStateRef.FieldDependencies.filter((d) => d == dataFieldId).length >= 2;
    }

    debounceCache[element.PublicId].isUnconditional = Boolean(
        debounceCache[element.PublicId].isUnconditional || isUnconditional
    );
    debounceCache[element.PublicId].isConditional = Boolean(
        debounceCache[element.PublicId].isConditional || isConditional
    );
    if (isUnconditional) {
        debounceCache[element.PublicId].unconditionalDataFieldIdList.push(dataFieldId);
    }
    commit(
        AUX_MUTATION,
        {
            elementId: element.PublicId,
            auxState: {
                incentiveDesigns: projectElementStateRef.auxState.incentiveDesigns,
                selectedDesignPublicId: projectElementStateRef.auxState.selectedDesignPublicId,
                isLoading: projectElementStateRef.auxState.isLoading || isUnconditional,
                isCalculating: projectElementStateRef.auxState.isCalculating || isConditional,
                userIsAdmin: projectElementStateRef.auxState.userIsAdmin
            }
        },
        { root: true }
    );

    if (debounceCache[element.PublicId].activeTimeout) {
        const timeoutToDelete = debounceCache[element.PublicId].activeTimeout;
        if (debounceCache[element.PublicId].operations[timeoutToDelete]) {
            debounceCache[element.PublicId].operations[timeoutToDelete].isCanceled = true;
        }
        clearTimeout(timeoutToDelete);
    }

    const id = delayedOperationIdGenerator.getNextId("incentiveDesignDependencyUpdate");

    const timeoutContext = {
        isUnconditional: debounceCache[element.PublicId].isUnconditional,
        isConditional: debounceCache[element.PublicId].isConditional,
        unconditionalDataFieldIdList: debounceCache[element.PublicId].unconditionalDataFieldIdList,
        isCanceled: false,
        id,
        timeoutId: null
    };

    debounceCache[element.PublicId].activeTimeout = setTimeout(async function () {
        try {
            if (timeoutContext.isUnconditional && !timeoutContext.isCanceled) {
                await doUnconditionalDependencyUpdate({
                    $app,
                    baseController,
                    element,
                    projectElementStateRef,
                    dataFieldIds: timeoutContext.unconditionalDataFieldIdList,
                    rootGetters,
                    dispatch,
                    commit
                });
            }
            if (timeoutContext.isConditional && !timeoutContext.isCanceled) {
                await doConditionalDependencyUpdate({
                    $app,
                    baseController,
                    element,
                    projectElementStateRef,
                    rootGetters,
                    dispatch
                });
            }
        } finally {
            resetDebounceCacheForElementId(element.PublicId);

            commit(
                AUX_MUTATION,
                {
                    elementId: element.PublicId,
                    auxState: {
                        incentiveDesigns: projectElementStateRef.auxState.incentiveDesigns,
                        selectedDesignPublicId: projectElementStateRef.auxState.selectedDesignPublicId,
                        isLoading: false,
                        isCalculating: false,
                        userIsAdmin: projectElementStateRef.auxState.userIsAdmin
                    }
                },
                { root: true }
            );
        }
    }, DEBOUNCE_DELAY);

    timeoutContext.timeoutId = debounceCache[element.PublicId].activeTimeout;
    debounceCache[element.PublicId].operations[timeoutContext.timeoutId] = timeoutContext;
}

async function doConditionalDependencyUpdate({
    $app,
    baseController,
    element,
    projectElementStateRef,
    rootGetters,
    dispatch
}) {
    // A conditional dependency update triggers an incentive recalculation if an incentive design has been
    //  selected and calculated, so display a dialog warning the user of this.
    // If there is no incentive option selected or there is an Admin override, then no recalculation is done,
    //  and so nothing is done here.

    if (
        projectElementStateRef.auxState.selectedDesignPublicId &&
        projectElementStateRef.auxState.selectedDesignPublicId !== NO_INCENTIVE_DESIGN &&
        projectElementStateRef.auxState.selectedDesignPublicId !== ADMIN_OVERRIDE_DESIGN
    ) {
        const detailsId = element.GroupDataFields[outputFields.CalculationDetails];
        const details = rootGetters[PROJECT_TYPES.getters.dataFieldValueFromId](detailsId).trim();

        // Don't do a new calculation of the incentive amount if the incentive selector is in the
        //  "not yet calculated" state, but update the amount if it has already been calculated
        if (details === "" || details === NOT_YET_CALCULATED_DETAILS) {
            return;
        }

        const amountId = element.GroupDataFields[outputFields.CalculatedAmount];
        const amount = convertToFiniteNumber(rootGetters[PROJECT_TYPES.getters.dataFieldValueFromId](amountId));

        if (isFiniteNumber(amount) && amount > 0) {
            // The change to this dependency value will force a recalculation of the incentive, so we want to advise
            //  the user of this.
            // If we are viewing the form from the Admin page, we also want to tell the admin that recalculating the
            //  incentive will require that the project be saved, and give them the opportunity to cancel -- unless
            //  the admin has already approved a save on this form, in which case we will just proceed based on that
            //  prior approval.
            const okResult = "OK";
            const cancelResult = "Cancel";
            let titleKey = i18nKey.incentiveRecalculationDialogTitle;
            let messageKey = i18nKey.incentiveRecalculationDialogMessage;
            let okTextKey = i18nKey.incentiveRecalculationDialogOkButtonLabel;
            const buttons = [];
            if (baseController.inAdminMode && !baseController.adminSaveConfirmed) {
                titleKey = i18nKey.incentiveRecalculationAdminDialogTitle;
                messageKey = i18nKey.incentiveRecalculationAdminDialogMessage;
                okTextKey = i18nKey.incentiveRecalculationAdminDialogConfirmButtonLabel;
                buttons.push({
                    text: i18nMessageText($app, i18nKey.incentiveRecalculationAdminDialogCancelButtonLabel),
                    result: cancelResult
                });
            }
            buttons.push({
                text: i18nMessageText($app, okTextKey),
                primary: true,
                result: okResult,
                testRole: RECALC_CONFIRM_ROLE
            });

            // It is possible that we try to show the same alert twice during a single update;
            //  silently fail the first one, but make sure the second one still works.
            // The showDialog code takes care of most of this, we just need to catch the rejected promise here.
            let result = "";
            try {
                result = await $app.showDialog({
                    title: i18nMessageText($app, titleKey),
                    message: i18nMessageText($app, messageKey),
                    buttons: buttons,
                    testRole: RECALC_DIALOG_ROLE
                });
            } catch (e) {
                return;
            }

            if (baseController.inAdminMode && !baseController.adminSaveConfirmed) {
                // This button click can only happen if we are showing the admin-page version of the dialog
                if (result === cancelResult) {
                    await clearOutputValuesAsync(element, dispatch);
                    return;
                }

                // Remember that the admin has confirmed the form save once and so does not need
                //  to be asked again
                dispatch(PROJECT_FORM_CONTROLLER_TYPES.actions.confirmAdminSave, null, { root: true });
            }
        }

        const error = await dispatch(
            PROJECT_FORM_TYPES.actions.updateIncentive,
            {
                elementId: element.PublicId,
                selectedDesignPublicId: projectElementStateRef.auxState.selectedDesignPublicId
            },
            { root: true }
        );

        if (error) {
            // If incentive is already uncalculated or the error is that the PV system needs to be recalculated,
            // don't display the error dialog
            if (isFiniteNumber(amount) && error !== i18nMessageText($app, i18nKey.pvSystemUncalculatedErrorMessage)) {
                const title = i18nMessageText($app, i18nKey.incentiveCalculationFailedDialogTitle);
                await $app.showErrorDialog(title, error);
                throw error;
            }
        }
    }
}

async function doUnconditionalDependencyUpdate({
    $app,
    baseController,
    element,
    projectElementStateRef,
    dataFieldIds,
    rootGetters,
    dispatch,
    commit
}) {
    // If the triggering dependency update is unconditional, it could change the array of
    //  available Incentive Design options, which could mean that the previous selected
    //  option is no longer available.
    // If the form is being viewed from the Admin page we need to advise the admin that
    //  this change will require a form save, and give them an opportunity to cancel the save.
    //  However, if they cancel their previous incentive selection and calculation (if any)
    //  will be lost because we cannot determine whether the application is still eligible
    //  for that incentive.  (Note that if the admin has already confirmed a save on the form we
    //  will proceed with that prior approval.)
    if (baseController.inAdminMode && !baseController.adminSaveConfirmed) {
        const cancelResult = "CANCEL";
        const okResult = "OK";
        const result = await $app.showDialog({
            title: i18nMessageText($app, i18nKey.incentiveEligibilityAdminDialogTitle),
            message: i18nMessageText($app, i18nKey.incentiveEligibilityAdminDialogMessage),
            buttons: [
                {
                    text: i18nMessageText($app, i18nKey.incentiveEligibilityAdminDialogCancelButtonLabel),
                    result: cancelResult
                },
                {
                    text: i18nMessageText($app, i18nKey.incentiveEligibilityAdminDialogConfirmButtonLabel),
                    primary: true,
                    result: okResult
                }
            ]
        });
        if (result === cancelResult) {
            commit(
                AUX_MUTATION,
                {
                    elementId: element.PublicId,
                    auxState: {
                        incentiveDesigns: projectElementStateRef.auxState.incentiveDesigns,
                        selectedDesignPublicId: NO_INCENTIVE_DESIGN,
                        isLoading: false,
                        isCalculating: false,
                        userIsAdmin: projectElementStateRef.auxState.userIsAdmin
                    }
                },
                { root: true }
            );

            await clearOutputValuesAsync(element, dispatch);

            return;
        }

        // Remember that the admin has confirmed the form save once and so does not need
        //  to be asked again
        dispatch(PROJECT_FORM_CONTROLLER_TYPES.actions.confirmAdminSave, null, { root: true });
    }

    commit(
        AUX_MUTATION,
        {
            elementId: element.PublicId,
            auxState: {
                incentiveDesigns: projectElementStateRef.auxState.incentiveDesigns,
                selectedDesignPublicId: projectElementStateRef.auxState.selectedDesignPublicId,
                isLoading: true,
                isCalculating: projectElementStateRef.auxState.isCalculating,
                userIsAdmin: projectElementStateRef.auxState.userIsAdmin
            }
        },
        { root: true }
    );

    try {
        await dispatch(PROJECT_FORM_CONTROLLER_TYPES.actions.saveFormData, null, { root: true });
    } catch {
        throw new Error("Save Form Data failed.");
    }

    // Get the current incentive amount
    const amountPublicId = element.GroupDataFields[outputFields.CalculatedAmount];
    const amount = convertToFiniteNumber(rootGetters[PROJECT_TYPES.getters.dataFieldValueFromId](amountPublicId));

    // If this was triggered by a PV System becoming uncalculated, the Calculation Amount and Details should
    //  be cleared, but the Incentive Design options list should not be rebuilt, nor should the currently
    //  selected option be cleared.
    let designs = projectElementStateRef.auxState.incentiveDesigns;
    let rebuildIncentiveDesignOptions = true;
    let clearCurrentIncentiveAmount = false;
    let warning = "";

    if (projectElementStateRef.auxState.selectedDesignPublicId === NO_INCENTIVE_DESIGN) {
        // If no incentive design was selected there shouldn't be an amount, but if there is it should be cleared
        clearCurrentIncentiveAmount = isFiniteNumber(amount);
    } else {
        // See if the triggering data field is part of the PV system associated with the selected Incentive Design
        const previousDesign = designs.find(function (d) {
            return d.PublicId === projectElementStateRef.auxState.selectedDesignPublicId;
        });

        if (previousDesign) {
            const associatedPvSystemPublicId = previousDesign.PvSystemPublicId;
            if (associatedPvSystemPublicId) {
                const pvSystemDfd =
                    rootGetters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](associatedPvSystemPublicId);
                dataFieldIds.forEach((dataFieldId) => {
                    const triggeringDfd = rootGetters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](dataFieldId);
                    if (
                        pvSystemDfd.DataFieldGroupId &&
                        pvSystemDfd.DataFieldGroupId === triggeringDfd.DataFieldGroupId
                    ) {
                        // The data field that triggered this update is part of the PvSystem associated with the previously
                        //  selected IncentiveDesign
                        // If the data field value is null, that implies that the PvSystem is uncalculated;
                        //  rebuild the incentive design option list only if the PvSystem *is* calculated
                        const dataFieldValue = rootGetters[PROJECT_TYPES.getters.dataFieldValueFromId](dataFieldId);
                        const dataIsEmpty =
                            dataFieldValue === null || dataFieldValue === undefined || dataFieldValue === "";
                        rebuildIncentiveDesignOptions = rebuildIncentiveDesignOptions && !dataIsEmpty;
                        // But clear the current incentive amount if the PvSystem is uncalculated
                        clearCurrentIncentiveAmount = clearCurrentIncentiveAmount || dataIsEmpty;
                        if (clearCurrentIncentiveAmount && isFiniteNumber(amount) && amount > 0) {
                            warning = i18nMessageText($app, i18nKey.pvSystemCalculationRequiredMessage);
                        }
                    }
                });
            }
        }
    }

    let incentiveDesignPublicId = projectElementStateRef.auxState.selectedDesignPublicId;
    if (rebuildIncentiveDesignOptions) {
        // Get a new set of Incentive Design options
        const result = await rebuildIncentiveDesignOptionList({
            $app,
            formElementId: element.PublicId,
            previousDesigns: projectElementStateRef.auxState.incentiveDesigns,
            previousOptionPublicId: projectElementStateRef.auxState.selectedDesignPublicId,
            dispatch
        });

        designs = result.designs;
        // The previously selected incentive design is no longer an option
        if (result.optionsHaveChanged && result.previousOptionIsUnavailable) {
            incentiveDesignPublicId = NO_INCENTIVE_DESIGN;
            clearCurrentIncentiveAmount = true;
            warning = i18nMessageText($app, i18nKey.projectIsIneligibleForSelectedIncentiveMessage);
        }
    }

    // Update the Incentive Selector auxState
    commit(
        AUX_MUTATION,
        {
            elementId: element.PublicId,
            auxState: {
                incentiveDesigns: designs,
                selectedDesignPublicId: incentiveDesignPublicId,
                isLoading: false,
                isCalculating: projectElementStateRef.auxState.isCalculating,
                userIsAdmin: projectElementStateRef.auxState.userIsAdmin
            }
        },
        { root: true }
    );

    if (warning) {
        if (clearCurrentIncentiveAmount) {
            await clearOutputValuesAsync(element, dispatch);
            if (isFiniteNumber(amount) && amount > 0) {
                warning += " " + i18nMessageText($app, i18nKey.previousIncentiveAmountClearedMessage);
            }
            const okResult = "OK";
            await $app.showDialog({
                title: i18nMessageText($app, i18nKey.incentiveChangeWarningDialogTitle),
                message: warning,
                buttons: [
                    {
                        text: i18nMessageText($app, i18nKey.incentiveChangeWarningConfirmButtonLabel),
                        primary: true,
                        result: okResult,
                        testRole: rebuildIncentiveDesignOptions ? ELIGIBILITY_CONFIRM_ROLE : RECALC_CONFIRM_ROLE
                    }
                ],
                testRole: rebuildIncentiveDesignOptions ? ELIGIBILITY_DIALOG_ROLE : RECALC_DIALOG_ROLE
            });
        }
    }
}

async function rebuildIncentiveDesignOptionList({
    $app,
    formElementId,
    previousDesigns,
    previousOptionPublicId,
    dispatch
}) {
    const result = await dispatch(
        PROJECT_FORM_TYPES.actions.getIncentiveDesignOptions,
        {
            formElementId: formElementId,
            incentiveDesignId: previousOptionPublicId
        },
        { root: true }
    );

    if (result.hasError) {
        await $app.showErrorDialog("Error Retrieving Incentive Options", result.error);
        throw result.error;
    }

    let newDesigns = previousDesigns;
    let optionsHaveChanged = false;
    // Begin by assuming the previous selection is unavailable as long as there *was* a previous selection
    let previousOptionIsUnavailable = previousOptionPublicId !== NO_INCENTIVE_DESIGN;

    newDesigns = result.Data.DesignInfoList;

    // See if the previous selection is in the new option list
    if (previousOptionIsUnavailable) {
        const match = newDesigns.find(function (d) {
            return d.PublicId === previousOptionPublicId;
        });
        if (match) {
            previousOptionIsUnavailable = false;
        }
    }

    // See if the new option list is different from the previous list
    // ...If they have different lengths, obviously *something* changed
    optionsHaveChanged = previousDesigns.length !== newDesigns.length;

    newDesigns.forEach((d) => {
        // Make sure each of the new designs is in the previous list; stop checking
        //  if an exception is found
        if (!optionsHaveChanged) {
            const match = previousDesigns.find(function (p) {
                return p.PublicId === d.PublicId;
            });
            if (!match) {
                optionsHaveChanged = true;
            }
        }
    });

    const obj = {
        status: "success",
        designs: newDesigns,
        optionsHaveChanged: optionsHaveChanged,
        previousOptionIsUnavailable: previousOptionIsUnavailable
    };

    return obj;
}

function getAuxProjectElementState(element, projectElementStateRef /*, Project*/) {
    let designs = [];
    let selectedDesignPublicId = NO_INCENTIVE_DESIGN;
    let userIsAdmin = false;

    if (projectElementStateRef.hasOwnProperty("AuxiliaryModel")) {
        designs = projectElementStateRef.AuxiliaryModel.DesignInfoList;
        selectedDesignPublicId = projectElementStateRef.AuxiliaryModel.selectedDesignPublicId;
        userIsAdmin = projectElementStateRef.AuxiliaryModel.userIsAdmin;
    }

    return {
        incentiveDesigns: designs,
        selectedDesignPublicId: selectedDesignPublicId,
        isLoading: false,
        isCalculating: false,
        userIsAdmin: userIsAdmin
    };
}

function isComplete(element, getters) {
    const designId = element.GroupDataFields[inputFields.SelectedIncentiveDesign];
    const design = getters[PROJECT_TYPES.getters.dataFieldValueFromId](designId);
    const detailsId = element.GroupDataFields[outputFields.CalculationDetails];
    const details = getters[PROJECT_TYPES.getters.dataFieldValueFromId](detailsId);

    return design && design !== NO_INCENTIVE_DESIGN && details && details !== NOT_YET_CALCULATED_DETAILS;
}

export const incentiveSelectorController = {
    clearOutputValuesAsync,
    onDependencyUpdate,
    getAuxProjectElementState,
    isComplete
};
